<template>
  <ModalDialog
    :header-text="`Delete user ${user.UserName}`"
    :canBeClosed="!userDeleting"
    contentClass="delete-modal-content"
    @close-modal="closeModal"
    open
  >
    <template v-slot:content>
      <p class="content-title">Are you sure you want to delete this user?</p>
      <div class="content-actions">
        <Button
          type="button"
          theme-color="error"
          @click="deleteUserAsync"
          :disabled="userDeleting"
          >Delete</Button
        >
        <Button
          type="button"
          theme-color="success"
          @click="closeModal(!userDeleting, false)"
          >Cancel</Button
        >
      </div>
    </template>
  </ModalDialog>
</template>

<script setup lang="ts">
import type { MemberShipUserPlus } from "@/Domain/MemberShipUserPlus";
import { Button } from "@progress/kendo-vue-buttons";
import ModalDialog from "@/shared/ui/modal/ModalDialog.vue";
import { api } from "@/api";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import { useNotificationStore } from "@/stores/useNotificationStore";
import { NotificationTypeEnum } from "@/Domain/NotificationTypeEnum";
import { ref } from "vue";
import { useMainStore } from "@/stores/useMainStore";

const main = useMainStore();
const notificationStore = useNotificationStore();

const props = defineProps<{
  user: MemberShipUserPlus;
}>();

const emit = defineEmits<{
  (e: "closeModal", userDeleted: boolean): void;
}>();

const userDeleting = ref(false);

const deleteUserAsync = async function () {
  if (userDeleting.value) return;

  userDeleting.value = true;

  await handleResponsePromiseAsync(
    api.post("DeleteUser", {
      CompanyLogin: main.companyLogin,
      UserName: props.user.UserName,
    }),
    () => {
      notificationStore.notify({
        type: NotificationTypeEnum.success,
        message: "User deleted successfully",
        duration: 5000,
      });
      closeModal(true, true);
    },
    "UserDeleteModal.vue",
    "deleteUserAsync",
    (error, response) => {
      if (response?.error) {
        notificationStore.notify({
          type: NotificationTypeEnum.error,
          message: response.error.toString(),
          duration: 5000,
        });
        return;
      }
      notificationStore.notify({
        type: NotificationTypeEnum.error,
        message: `Error deleting user: ${error}`,
        duration: 5000,
      });
    }
  );
  userDeleting.value = false;
};

const closeModal = function (closed: boolean, userDeleted: boolean = false) {
  if (!closed) {
    notificationStore.notify({
      type: NotificationTypeEnum.info,
      message: "Please wait while user is being deleted",
      duration: 5000,
    });
    return;
  }
  emit("closeModal", userDeleted);
};
</script>

<style lang="scss">
.delete-modal-content {
  display: flex;
  flex-direction: column;

  .content {
    &-title {
      font-size: 1rem;
      font-weight: 600;
      margin: 1rem 0;
    }

    &-actions {
      display: flex;
      justify-content: flex-end;
      gap: 1rem;
      padding: 5px;
    }
  }
}
</style>
